import { useState, useRef, useEffect, useCallback } from 'react';
import api from './api';

const CALL_STATE = {
  IDLE: 'idle',
  CALLING: 'calling',       // outgoing ringing
  INCOMING: 'incoming',     // incoming ringing
  CONNECTING: 'connecting', // accepted, establishing
  ACTIVE: 'active',         // media flowing
};

// Single persistent AudioContext for the whole app session — avoids Chrome's 6-context limit
let _ringCtx = null;
let _ringOsc = null;
let _ringGain = null;

function getRingCtx() {
  if (!_ringCtx) {
    try { _ringCtx = new (window.AudioContext || window.webkitAudioContext)(); }
    catch { return null; }
  }
  if (_ringCtx.state === 'suspended') _ringCtx.resume().catch(() => {});
  return _ringCtx;
}

function ringtoneStart() {
  const ctx = getRingCtx();
  if (!ctx) return;
  if (_ringOsc) return; // already playing
  _ringGain = ctx.createGain();
  _ringGain.connect(ctx.destination);
  _ringOsc = ctx.createOscillator();
  _ringOsc.type = 'sine';
  _ringOsc.frequency.value = 440;
  _ringOsc.connect(_ringGain);
  // Envelope: 1.5s on, 2.5s off, looped via gain scheduling
  const now = ctx.currentTime;
  const period = 4.0;
  for (let t = 0; t < 60; t += period) {
    _ringGain.gain.setValueAtTime(0.08, now + t);
    _ringGain.gain.setValueAtTime(0.08, now + t + 1.5);
    _ringGain.gain.linearRampToValueAtTime(0, now + t + 1.7);
    _ringGain.gain.setValueAtTime(0, now + t + period);
  }
  _ringOsc.start();
}

function ringtoneStop() {
  if (_ringOsc) { try { _ringOsc.stop(); } catch {} _ringOsc.disconnect(); _ringOsc = null; }
  if (_ringGain) { _ringGain.disconnect(); _ringGain = null; }
}

export function useCall(socket, currentUser) {
  const [state, setState] = useState(CALL_STATE.IDLE);
  const [call, setCall] = useState(null); // { callId, peer, isVideo, isCaller, chatId }
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [muted, setMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const [remoteState, setRemoteState] = useState({ muted: false, videoOff: false });
  const [duration, setDuration] = useState(0);
  const [callError, setCallError] = useState(null);

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const pendingIceRef = useRef([]);
  const durationTimerRef = useRef(null);
  const ringInterval = useRef(null);
  const iceServersRef = useRef(null);
  const callRef = useRef(null);
  const socketRef = useRef(null);

  // Keep refs in sync with state/props
  useEffect(() => { callRef.current = call; }, [call]);
  useEffect(() => { socketRef.current = socket; }, [socket]);

  // Fetch ICE servers
  const fetchIce = useCallback(async () => {
    if (iceServersRef.current) return iceServersRef.current;
    try {
      const { data } = await api.get('/calls/ice');
      iceServersRef.current = data.iceServers;
      return data.iceServers;
    } catch {
      return [{ urls: 'stun:stun.l.google.com:19302' }];
    }
  }, []);

  const startRinging = () => ringtoneStart();
  const stopRinging = () => ringtoneStop();

  // Build peer connection
  const createPeer = async () => {
    const iceServers = await fetchIce();
    const pc = new RTCPeerConnection({ iceServers });
    pcRef.current = pc;

    pc.ontrack = (e) => {
      console.log('[call] ontrack:', e.track.kind, 'streams:', e.streams?.length);
      // Use the peer's own stream directly — don't wrap in new MediaStream
      // (the wrapping pattern breaks some browsers that tie tracks to original stream)
      if (e.streams && e.streams[0]) {
        setRemoteStream(e.streams[0]);
      }
    };

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        const current = callRef.current;
        if (current && socketRef.current) {
          socketRef.current.emit('call:ice', {
            callId: current.callId,
            toUserId: current.peer.id,
            candidate: e.candidate,
          });
        }
      }
    };

    let iceRestartTimer = null;
    pc.oniceconnectionstatechange = async () => {
      console.log('[call] iceConnectionState:', pc.iceConnectionState);
      if (pc.iceConnectionState === 'disconnected') {
        // Give it 3s to recover on its own before restart
        iceRestartTimer = setTimeout(async () => {
          if (pc.iceConnectionState === 'disconnected' && callRef.current?.isCaller) {
            console.log('[call] Attempting ICE restart');
            try {
              const offer = await pc.createOffer({ iceRestart: true });
              await pc.setLocalDescription(offer);
              socketRef.current?.emit('call:offer', {
                callId: callRef.current.callId,
                toUserId: callRef.current.peer.id,
                sdp: offer,
              });
            } catch (err) { console.error('[call] ICE restart failed:', err); }
          }
        }, 3000);
      } else if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        if (iceRestartTimer) { clearTimeout(iceRestartTimer); iceRestartTimer = null; }
      } else if (pc.iceConnectionState === 'failed') {
        setCallError('Соединение потеряно');
        setTimeout(() => endCall(true), 2000);
      }
    };

    pc.onconnectionstatechange = () => {
      console.log('[call] connectionState:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        setState(CALL_STATE.ACTIVE);
        startDurationTimer();
      } else if (pc.connectionState === 'failed') {
        setCallError('Соединение потеряно');
        setTimeout(() => endCall(true), 2000);
      }
    };

    return pc;
  };

  // Get user media
  const getMedia = async (video) => {
    const constraints = {
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: video ? { width: 1280, height: 720 } : false,
    };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    localStreamRef.current = stream;
    setLocalStream(stream);
    return stream;
  };

  // Add tracks to peer
  const addTracks = (pc, stream) => {
    stream.getTracks().forEach(track => pc.addTrack(track, stream));
  };

  // Apply pending ICE after remote description set
  const applyPendingIce = async (pc) => {
    for (const c of pendingIceRef.current) {
      try { await pc.addIceCandidate(c); } catch {}
    }
    pendingIceRef.current = [];
  };

  // Start duration timer
  const startDurationTimer = () => {
    if (durationTimerRef.current) return;
    setDuration(0);
    durationTimerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
  };

  // Initiate call (caller)
  const initiateCall = useCallback(async (peer, isVideo, chatId) => {
    if (state !== CALL_STATE.IDLE) return;
    setCallError(null);
    const callId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    setCall({ callId, peer, isVideo, isCaller: true, chatId });
    setState(CALL_STATE.CALLING);

    try {
      await getMedia(isVideo);
      socket.emit('call:invite', {
        toUserId: peer.id,
        callId,
        chatId,
        isVideo,
      });
      startRinging();
    } catch (err) {
      setCallError('Не удалось получить доступ к микрофону/камере');
      setTimeout(() => endCall(true), 2000);
    }
  }, [state, socket]);

  // Accept incoming call (callee)
  const acceptCall = useCallback(async () => {
    if (state !== CALL_STATE.INCOMING || !call) return;
    stopRinging();
    setState(CALL_STATE.CONNECTING);
    try {
      const stream = await getMedia(call.isVideo);
      const pc = await createPeer();
      addTracks(pc, stream);

      // Tell caller we accepted — they'll create offer
      socket.emit('call:accept', { callId: call.callId, toUserId: call.peer.id });
    } catch (err) {
      setCallError('Не удалось получить доступ к микрофону/камере');
      setTimeout(() => endCall(true), 2000);
    }
  }, [state, call, socket]);

  // Reject incoming
  const rejectCall = useCallback(() => {
    if (!call) return;
    stopRinging();
    socket.emit('call:reject', { callId: call.callId, toUserId: call.peer.id, reason: 'rejected' });
    logCallEvent('declined', 0);
    resetCall();
  }, [call, socket, logCallEvent]);

  // Log call event to backend (async, fire-and-forget)
  const logCallEvent = useCallback((status, durationSec) => {
    const cur = callRef.current;
    if (!cur || !cur.chatId) return;
    import('./api').then(({ default: api }) => {
      api.post('/calls/log', {
        chatId: cur.chatId,
        callId: cur.callId,
        isVideo: !!cur.isVideo,
        status, // 'ended' | 'missed' | 'declined' | 'busy' | 'failed'
        durationSeconds: durationSec || 0,
        peerId: cur.peer?.id,
      }).catch(() => {});
    });
  }, []);

  // End call
  const endCall = useCallback((silent = false) => {
    if (!silent && call && socket) {
      socket.emit('call:end', { callId: call.callId, toUserId: call.peer.id });
    }
    // Log the event (only once per call — caller logs, callee will get it via socket broadcast)
    if (call && call.isCaller) {
      if (state === CALL_STATE.ACTIVE) logCallEvent('ended', duration);
      else if (state === CALL_STATE.CALLING) logCallEvent('missed', 0);
    }
    resetCall();
  }, [call, socket, state, duration, logCallEvent]);

  const resetCall = () => {
    stopRinging();
    if (durationTimerRef.current) { clearInterval(durationTimerRef.current); durationTimerRef.current = null; }
    if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    pendingIceRef.current = [];
    setLocalStream(null);
    setRemoteStream(null);
    setCall(null);
    setState(CALL_STATE.IDLE);
    setMuted(false);
    setVideoOff(false);
    setRemoteState({ muted: false, videoOff: false });
    setDuration(0);
    setTimeout(() => setCallError(null), 2000);
  };

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (!localStreamRef.current) return;
    const next = !muted;
    localStreamRef.current.getAudioTracks().forEach(t => t.enabled = !next);
    setMuted(next);
    if (call) socket.emit('call:state', { callId: call.callId, toUserId: call.peer.id, muted: next, videoOff });
  }, [muted, videoOff, call, socket]);

  // Toggle video
  const toggleVideo = useCallback(() => {
    if (!localStreamRef.current) return;
    const next = !videoOff;
    localStreamRef.current.getVideoTracks().forEach(t => t.enabled = !next);
    setVideoOff(next);
    if (call) socket.emit('call:state', { callId: call.callId, toUserId: call.peer.id, muted, videoOff: next });
  }, [videoOff, muted, call, socket]);

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    const onIncoming = async ({ callId, chatId, isVideo, from }) => {
      if (state !== CALL_STATE.IDLE) {
        // Busy — auto-reject
        socket.emit('call:reject', { callId, toUserId: from.id, reason: 'busy' });
        return;
      }
      setCall({ callId, peer: from, isVideo, isCaller: false, chatId });
      setState(CALL_STATE.INCOMING);
      startRinging();
    };

    const onAccepted = async ({ callId }) => {
      const cur = callRef.current;
      if (!cur || cur.callId !== callId || !cur.isCaller) return;
      stopRinging();
      setState(CALL_STATE.CONNECTING);
      try {
        const pc = await createPeer();
        if (localStreamRef.current) addTracks(pc, localStreamRef.current);
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.emit('call:offer', { callId, toUserId: cur.peer.id, sdp: offer });
      } catch (err) {
        console.error('[call] onAccepted failed:', err);
        setCallError('Ошибка соединения');
        setTimeout(() => endCall(true), 2000);
      }
    };

    const onRejected = ({ callId, reason }) => {
      if (!call || call.callId !== callId) return;
      setCallError(reason === 'busy' ? 'Занят' : 'Отклонён');
      setTimeout(() => resetCall(), 1500);
    };

    const onOffer = async ({ callId, sdp }) => {
      const cur = callRef.current;
      if (!cur || cur.callId !== callId || cur.isCaller) return;
      const pc = pcRef.current;
      if (!pc) return;
      try {
        await pc.setRemoteDescription(sdp);
        await applyPendingIce(pc);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit('call:answer', { callId, toUserId: cur.peer.id, sdp: answer });
      } catch (err) {
        console.error('[call] onOffer failed:', err);
      }
    };

    const onAnswer = async ({ callId, sdp }) => {
      const cur = callRef.current;
      if (!cur || cur.callId !== callId || !cur.isCaller) return;
      const pc = pcRef.current;
      if (!pc) return;
      try {
        await pc.setRemoteDescription(sdp);
        await applyPendingIce(pc);
      } catch (err) {
        console.error('[call] onAnswer failed:', err);
      }
    };

    const onIce = async ({ callId, candidate }) => {
      const cur = callRef.current;
      if (!cur || cur.callId !== callId) return;
      const pc = pcRef.current;
      if (!pc || !pc.remoteDescription) {
        pendingIceRef.current.push(candidate);
      } else {
        try { await pc.addIceCandidate(candidate); } catch (e) { console.warn('[call] addIceCandidate:', e.message); }
      }
    };

    const onEnded = ({ callId }) => {
      const cur = callRef.current;
      if (!cur || cur.callId !== callId) return;
      setCallError('Звонок завершён');
      setTimeout(() => resetCall(), 1000);
    };

    const onRemoteState = ({ callId, muted: m, videoOff: v }) => {
      const cur = callRef.current;
      if (!cur || cur.callId !== callId) return;
      setRemoteState({ muted: m, videoOff: v });
    };

    socket.on('call:incoming', onIncoming);
    socket.on('call:accepted', onAccepted);
    socket.on('call:rejected', onRejected);
    socket.on('call:offer', onOffer);
    socket.on('call:answer', onAnswer);
    socket.on('call:ice', onIce);
    socket.on('call:ended', onEnded);
    socket.on('call:state', onRemoteState);

    return () => {
      socket.off('call:incoming', onIncoming);
      socket.off('call:accepted', onAccepted);
      socket.off('call:rejected', onRejected);
      socket.off('call:offer', onOffer);
      socket.off('call:answer', onAnswer);
      socket.off('call:ice', onIce);
      socket.off('call:ended', onEnded);
      socket.off('call:state', onRemoteState);
    };
  }, [socket, state, call]);

  return {
    state,
    call,
    localStream,
    remoteStream,
    muted,
    videoOff,
    remoteState,
    duration,
    callError,
    initiateCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleVideo,
    isActive: state !== CALL_STATE.IDLE,
  };
}

export { CALL_STATE };
